import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Form, Input, Select, Button, Space, Popconfirm, message,
} from 'antd';
import DraggableModal from '../../components/DraggableModal';
import { PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import api from '../../api';
import PartitionDetails from './PartitionDetails';

export default function Partitions() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [locations, setLocations] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [activeView, setActiveView] = useState('manage');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/partitions');
      const all = Array.isArray(res) ? res : [];
      const kw = (searchName || '').trim();
      const rows = kw ? all.filter(r => String(r.name || '').includes(kw)) : all;
      setData(rows);
      setSelectedRowKeys([]);
    } catch {
      message.error('获取分区列表失败');
    }
    setLoading(false);
  }, [searchName]);

  const fetchLocations = useCallback(async () => {
    try {
      const locRes = await api.get('/asset-locations');
      setLocations(Array.isArray(locRes) ? locRes : []);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => { fetchData(); fetchLocations(); }, [fetchData, fetchLocations]);

  const handleCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      location_ids: record.location_ids || [],
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await api.put(`/partitions/${editing.id}`, values);
        message.success('更新成功');
      } else {
        await api.post('/partitions', values);
        message.success('创建成功');
      }
      setModalOpen(false);
      fetchData();
    } catch {
      // validation or api error
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.post('/partitions/delete-batch', { scope: 'ids', ids: [id] });
      message.success(`删除成功（${res?.deleted_count || 0} 条）`);
      fetchData();
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedRowKeys.length) {
      message.warning('请先勾选要删除的分区');
      return;
    }
    try {
      const res = await api.post('/partitions/delete-batch', { scope: 'ids', ids: selectedRowKeys });
      message.success(`批量删除成功（${res?.deleted_count || 0} 条）`);
      fetchData();
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const locationOptions = locations.map(l => ({
    value: l.id,
    label: l.location_name,
  }));

  const saveOrder = async (rows) => {
    try {
      setSavingOrder(true);
      await api.post('/partitions/reorder', { ids: rows.map(r => r.id) });
      message.success('拖拽排序已保存');
      setData(rows);
    } catch (err) {
      message.error(err.message || '保存排序失败');
      fetchData();
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDropRow = (targetId) => {
    if (draggingId == null || draggingId === targetId) return;
    if ((searchName || '').trim()) {
      message.warning('请先清空搜索条件，再进行拖拽排序');
      setDraggingId(null);
      return;
    }
    const fromIndex = data.findIndex(r => r.id === draggingId);
    const toIndex = data.findIndex(r => r.id === targetId);
    if (fromIndex < 0 || toIndex < 0) {
      setDraggingId(null);
      return;
    }
    const next = [...data];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setDraggingId(null);
    saveOrder(next);
  };

  const columns = [
    {
      title: '拖拽',
      key: 'drag',
      width: 80,
      render: (_, record) => (
        <span
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move';
            setDraggingId(record.id);
          }}
          onDragEnd={() => setDraggingId(null)}
          style={{ cursor: 'grab', color: '#999', userSelect: 'none' }}
          title="拖拽排序"
        >
          ☰
        </span>
      ),
    },
    { title: '分区名称', dataIndex: 'name', key: 'name' },
    {
      title: '展示地点',
      dataIndex: 'location_names',
      key: 'location_names',
      render: (v) => (v || []).join('、') || '-',
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确认删除?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {activeView === 'details' ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => setActiveView('manage')}>
              返回
            </Button>
          </div>
          <PartitionDetails />
        </>
      ) : (
        <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>分区管理</h2>
        <Space>
          <Input
            placeholder="搜索分区名称"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onPressEnter={fetchData}
            allowClear
            style={{ width: 220 }}
          />
          <Button onClick={fetchData}>搜索</Button>
          <Popconfirm title={`仅删除已勾选项：确认删除 ${selectedRowKeys.length} 个分区？`} onConfirm={handleDeleteSelected} disabled={!selectedRowKeys.length}>
            <Button danger disabled={!selectedRowKeys.length}>删除已勾选</Button>
          </Popconfirm>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新建分区</Button>
          <Button onClick={() => setActiveView('details')}>分区详情</Button>
        </Space>
      </div>
      <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
        所有分区在同一页面展示；可直接拖拽左侧“☰”调整顺序，无需再填写 0/1/2/3 排序值。
      </div>

      <Table
        rowKey="id"
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
        columns={columns}
        dataSource={data}
        loading={loading || savingOrder}
        pagination={false}
        onRow={(record) => ({
          onDragOver: (e) => e.preventDefault(),
          onDrop: () => handleDropRow(record.id),
        })}
        rowClassName={(record) => (record.id === draggingId ? 'partition-dragging-row' : '')}
      />

      <DraggableModal
        title={editing ? '编辑分区' : '新建分区'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="分区名称" rules={[{ required: true, message: '请输入分区名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="location_ids" label="展示地点（存放位置）">
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              options={locationOptions}
              placeholder="请选择地点"
            />
          </Form.Item>
        </Form>
      </DraggableModal>
        </>
      )}
    </div>
  );
}
